import React from 'react';
import {Button, Collapse, Icon, Tag, message} from 'antd';
import {PersonDeletionImpact} from './operation';
import './deletion-impact.less';

/** Copies a persisted record ID without triggering its containing item or collapse panel. */
function CopyRecordID({id}: {id: string}) {
    return <Button
        className="person-deletion-impact__copy-id"
        size="small"
        icon="copy"
        title="Copy record ID"
        aria-label={`Copy record ID ${id}`}
        onClick={async event => {
            event.stopPropagation();
            try {
                await navigator.clipboard.writeText(id);
                message.success('Record ID copied.');
            } catch (_) {
                message.error('Unable to copy record ID.');
            }
        }}
    />;
}

/** Presents the server's affected records without duplicating its deletion policy in the browser. */
export default function DeletionImpact({preview}: {preview: PersonDeletionImpact}) {
    const header = (label: string, count: number, action = 'Delete') => (
        <span className="person-deletion-impact__header">
            <span>{label} <strong>({count})</strong></span>
            <Tag color={count ? (action === 'Delete' ? 'red' : 'blue') : undefined}>{count ? action : 'None'}</Tag>
        </span>
    );
    return (
        <div className="person-deletion-impact">
            <Collapse defaultActiveKey={['person', 'passengers']}>
                <Collapse.Panel key="person" header={header('Person', 1)}>
                    <div className="person-deletion-impact__record">
                        <strong>{preview.personName}</strong>
                        {preview.personID && <CopyRecordID id={preview.personID} />}
                    </div>
                </Collapse.Panel>
                <Collapse.Panel key="passengers" header={header('Passengers', preview.passengers.length)}>
                    {preview.passengers.length ? (
                        <ul className="person-deletion-impact__items">
                            {preview.passengers.map(passenger => (
                                <li key={passenger.id}>
                                    <div>
                                        <strong><Icon type="user" />{' '}
                                            {passenger.retired ? 'Retired passenger' : passenger.live ? 'Live passenger' : 'Scheduled passenger'}
                                        </strong>
                                        <div>Customer: {passenger.customerName || 'Unavailable'}</div>
                                        <div>Transporter: {passenger.transporterName || 'Unavailable'}</div>
                                        <div>Employer: {passenger.employerName || 'Unavailable'}</div>
                                        <div>Scheduled flight date: {passenger.scheduledFlightDate
                                            ? <time>{passenger.scheduledFlightDate}</time> : 'Not scheduled'}</div>
                                        <div>Current flight: {passenger.externalOrganization ? 'External organization' : passenger.currentFlight
                                            ? passenger.currentFlight.desig || passenger.currentFlight.id
                                            : passenger.flightAssigned ? 'Details unavailable' : 'Unassigned'}</div>
                                        {passenger.currentFlight?.scheduledFlightDate && (
                                            <div>Flight date: <time>{passenger.currentFlight.scheduledFlightDate}</time></div>
                                        )}
                                    </div>
                                    {(passenger.blocker || passenger.flightAssigned) && <Tag color="orange">
                                        {passenger.blocker ? 'Blocks deletion' : 'Assigned to flight'}
                                    </Tag>}
                                    <CopyRecordID id={passenger.id} />
                                </li>
                            ))}
                        </ul>
                    ) : <span>No live, scheduled or retired passengers to delete.</span>}
                </Collapse.Panel>
                <Collapse.Panel key="credentials" header={header('Credentials', preview.credentials.length)}>
                    {preview.credentials.length ? (
                        <ul className="person-deletion-impact__items">
                            {preview.credentials.map(credential => (
                                <li key={credential.id}>
                                    <span><Icon type="idcard" /> {credential.label}</span>
                                    {credential.expiration && <span>Expires <time>{credential.expiration}</time></span>}
                                    <CopyRecordID id={credential.id} />
                                </li>
                            ))}
                        </ul>
                    ) : <span>No credentials to delete.</span>}
                </Collapse.Panel>
                <Collapse.Panel key="pob" header={header('POB records', preview.pobID ? 1 : 0)}>
                    {preview.pobID ? <div className="person-deletion-impact__record">
                        <span><Icon type="environment" /> Current POB record</span>
                        <CopyRecordID id={preview.pobID} />
                    </div> : <span>No POB record to delete.</span>}
                </Collapse.Panel>
                {(['whitelist', 'nonpreferred'] as const).map(kind => {
                    const lists = (preview.lists || []).filter(list => list.kind === kind);
                    const label = kind === 'whitelist' ? 'Whitelists' : 'Non-preferred lists';
                    return <Collapse.Panel key={kind} header={header(label, lists.length, 'Update')}>
                        {lists.length ? (
                            <ul className="person-deletion-impact__items">
                                {lists.map(list => <li key={list.id}>
                                    <span><Icon type="unordered-list" /> {list.label}</span>
                                    <span>Remove person reference</span>
                                    <CopyRecordID id={list.id} />
                                </li>)}
                            </ul>
                        ) : <span>No memberships to remove.</span>}
                    </Collapse.Panel>;
                })}
                {(['rig', 'quarters', 'pob'] as const).map(kind => {
                    const records = (preview.referenceUpdates || []).filter(item => item.kind === kind);
                    if (!records.length) return null;
                    const label = {rig: 'Rigs', quarters: 'Quarters assignments', pob: 'Other POB records'}[kind];
                    const description = {
                        rig: 'Remove person and POB references',
                        quarters: 'Remove essential-worker membership and assigned occupancy',
                        pob: 'Clear work-opposite POB reference'
                    }[kind];
                    return <Collapse.Panel key={`references-${kind}`} header={header(label, records.length, 'Update')}>
                        <ul className="person-deletion-impact__items">
                            {records.map(record => <li key={record.id}>
                                <span>{record.label}</span>
                                <span>{description}</span>
                                <CopyRecordID id={record.id} />
                            </li>)}
                        </ul>
                    </Collapse.Panel>;
                })}
            </Collapse>
        </div>
    );
}
